import React from 'react';
import styled, { createGlobalStyle } from 'styled-components';
import { BrowserRouter as Router, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { AuthProvider, useAuth } from './context/AuthContext';
import { canAccessPage } from './utils/roleUtils';
import LoginPage from './page/login';
import OtpPage from './page/OtpPage';
import HealthBook from './page/healthbook';
import Template from './page/template';
import HealthRecordsHistory from './page/HealthRecordsHistory';
import ExamineeGroup from './page/ExamineeGroup';
import Campaign from './page/Campaign';
import UserManagement from './page/UserManagement';
import DashboardOrganization from './page/DashboardOrganization';

const GlobalStyle = createGlobalStyle`
  * {
    margin: 0;
    padding: 0;
    box-sizing: border-box;
    font-family: 'Prompt', sans-serif;
  }
  
  html, body {
    font-family: 'Prompt', sans-serif;
    margin: 0;
    padding: 0;
    height: 100%;
    min-width: 1200px;
  }
  
  #root {
    height: 100vh;
    min-width: 1200px;
  }
`;

const AppContainer = styled.div`
  font-family: 'Prompt', sans-serif;
  min-height: 100vh;
`;

const ProtectedRoute = ({ children }) => {
  const { isAuthenticated, loading, user } = useAuth();
  const location = useLocation();

  if (loading) {
    return (
      <div style={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        height: '100vh',
        fontSize: '1.25rem',
        color: '#666'
      }}>
        กำลังโหลด...
      </div>
    );
  }

  if (sessionStorage.getItem("otp_pending")) {
    return <Navigate to="/otp" replace />;
  }

  if (!isAuthenticated || !user) {
    return <Navigate to="/login" replace />;
  }

  const userRoles = user?.role || [];
  const userLevel = user?.level || null;
  const currentPath = location.pathname;

  if (user && (!userRoles || userRoles.length === 0)) {
    console.warn('⚠️ User exists but no roles found, waiting for role data...');
    return (
      <div style={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        height: '100vh',
        fontSize: '1.25rem',
        color: '#666'
      }}>
        กำลังตรวจสอบสิทธิ์...
      </div>
    );
  }

  if (!canAccessPage(userRoles, currentPath, userLevel)) {
    console.warn('🚫 Access denied:', currentPath, 'User roles:', userRoles);

    if (userRoles.includes('super_admin')) {
      return <Navigate to="/health-groups" replace />;
    }
    if (userRoles.includes('manager')) {
      return <Navigate to="/health-groups" replace />;
    }
    if (userRoles.includes('analyze')) {
      return <Navigate to="/dashboard-organization" replace />;
    }
    if (userRoles.includes('nurse')) {
      return <Navigate to="/healthbook" replace />;
    }

    // admin_doctor ควรไปใช้ mfu-admin แทน
    console.error('❌ User has no valid roles for this site, redirecting to login');
    return <Navigate to="/login" replace />;
  }

  return children;
};

const AppRoutes = () => {
  return (
    <Routes>
      <Route path="/" element={<Navigate to="/login" replace />} />
      <Route path="/login" element={<LoginPage />} />
      <Route path="/otp" element={<OtpPage />} />
      <Route
        path="/health-groups"
        element={
          <ProtectedRoute>
            <ExamineeGroup />
          </ProtectedRoute>
        }
      />
      <Route
        path="/campaigns"
        element={
          <ProtectedRoute>
            <Campaign />
          </ProtectedRoute>
        }
      />
      <Route
        path="/user-management"
        element={
          <ProtectedRoute>
            <UserManagement />
          </ProtectedRoute>
        }
      />
      <Route
        path="/healthbook"
        element={
          <ProtectedRoute>
            <HealthBook />
          </ProtectedRoute>
        }
      />
      <Route
        path="/template"
        element={
          <ProtectedRoute>
            <Template />
          </ProtectedRoute>
        }
      />
      <Route
        path="/health-records-history"
        element={
          <ProtectedRoute>
            <HealthRecordsHistory />
          </ProtectedRoute>
        }
      />
      <Route
        path="/dashboard-organization"
        element={
          <ProtectedRoute>
            <DashboardOrganization />
          </ProtectedRoute>
        }
      />
    </Routes>
  );
};

const App = () => {
  return (
    <Router>
      <AuthProvider>
        <GlobalStyle />
        <AppContainer>
          <AppRoutes />
        </AppContainer>
      </AuthProvider>
    </Router>
  );
};

export default App;
